import React, { memo, useEffect, useState } from 'react'
import { FeedbackMessage } from 'components/marketplace/FeedbackMessage'
import moment from 'moment'
import Button from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import FormattedPhoneNumber from 'transfix-ui/components/FormattedPhoneNumber'
import { IconButton } from 'transfix-ui/components/IconButton'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Box, Typography } from 'transfix-ui/core'
import Skeleton from 'transfix-ui/core/Skeleton'
import { IMotorCarrierInsuranceContact } from 'types/graphqlTypes'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import {
  AutomobileLiability,
  GeneralLiability,
  CargoCoverageLiability,
  TrailerInterchangeCoverage,
  DishonestyCoverage,
  WorkersCompensation,
} from 'apps/carrier/components/Compliance/Insurance'
import { InsuranceDialog } from 'apps/carrier/components/Compliance/InsuranceDialog'
import { EditIcon } from 'transfix-icons'
import { INLINE_MESSAGE_VARIANTS, InlineMessage, brandColors } from 'transfix-ui'
import { useUserDetails } from 'utils/hooks'
import FORMATS from 'constants/datetime'
import { AddInsuranceContactInformationModal } from './AddInsuranceContactInformationModal'
import { useInsuranceContactsQuery } from './graphql/InsuranceContacts'

const InsuranceAgentCard = ({ agent }: { agent: IMotorCarrierInsuranceContact }) => {
  const { createdAt, email, fullName, insuranceDocuments, organizationName, phoneNumber } =
    agent || {}

  const formatedCreatedAtDate = moment(createdAt).format(FORMATS.dateFormat)
  return (
    <Box minWidth='330px' maxWidth='350px' mr='26px'>
      <Card>
        <Box minHeight='142px'>
          <Typography variant='subtitle1'>{fullName}</Typography>
          <Typography variant='body1'>{organizationName}</Typography>
          <Typography variant='body1'>
            <FormattedPhoneNumber phoneNumber={phoneNumber} />
          </Typography>
          <Typography variant='body1'>{email}</Typography>

          <Typography variant='body1'>
            {insuranceDocuments?.map(({ name, url }, i) => (
              <a href={url} type='tertiary' key={name} target='_blank' rel='noopener noreferrer'>
                Certificate of Insurance {!!i && i + 1}
              </a>
            ))}
          </Typography>
          <Typography variant='body1' color='textSecondary'>
            {formatedCreatedAtDate}
          </Typography>
        </Box>
      </Card>
    </Box>
  )
}

interface IInsuranceProps {
  carrierId?: number
  summary?: string
  reviewer?: {
    id?: number
    parentUserId?: number
    fullName?: string
  }
  insurances?: {}
  loading?: boolean
  error?: string
  getInsurance?: (...args: any[]) => any
  editInsurance?: (...args: any[]) => any
}

const Insurance: React.FunctionComponent<IInsuranceProps> = memo(
  ({ carrierId, editInsurance, error, getInsurance, insurances, loading, reviewer, summary }) => {
    const { userPermissions } = useUserDetails()
    const { canEditComplianceCarrierInfo } = userPermissions || {}

    useEffect(() => {
      if (carrierId && !insurances) {
        // @ts-ignore
        getInsurance(carrierId)
      }
    }, [carrierId])
    const { id, parentUserId } = reviewer || {}
    const ready = !(loading || error)

    const [isOpen, setIsOpen] = useState<boolean>(false)

    const {
      data: agentsData,
      error: agentsError,
      loading: agentsLoading,
      refetch: agentsRefetch,
    } = useInsuranceContactsQuery({
      variables: {
        carrierApplicationId: `${carrierId}`,
      },
    })
    const { insuranceContacts = [] } = agentsData || {}

    const addHandler = () => {
      setIsOpen(true)
    }

    const closeHandler = () => {
      setIsOpen(false)
    }

    const isHighwayMilestoneThreeInsuranceCardEnabled = useFeatureFlag(
      'highwayMilestoneThreeInsuranceCard'
    )
    return (
      <Card>
        <div className='pv5 row no-gutter'>
          <h3 className='fl mb0 mt2'>Insurance</h3>
          {ready && (
            <div className='fr'>
              <IconButton
                Icon={EditIcon}
                color='tertiary'
                onClick={editInsurance}
                data-test='edit-insurance'
                label='Edit'
                size='large'
              />
            </div>
          )}
          {carrierId && !isHighwayMilestoneThreeInsuranceCardEnabled && (
            <div className='col-xs-12'>
              <AddInsuranceContactInformationModal
                carrierApplicationId={carrierId}
                open={isOpen}
                onClose={closeHandler}
                refetch={agentsRefetch}
              />
              <Box display='flex' justifyContent='space-between' alignItems='center' mt='8px'>
                <Typography variant='subtitle1'>Agent Information</Typography>
                {canEditComplianceCarrierInfo ? (
                  <Button variant='text' onClick={addHandler}>
                    Submit Agent Contact Details
                  </Button>
                ) : (
                  <div />
                )}
              </Box>
              <Box m='16px 0' overflow='scroll' display='flex' width='100%'>
                {agentsLoading && (
                  <Box minWidth='330px' maxWidth='350px' mr='26px'>
                    <Card>
                      <Box minHeight='142px' display='grid' gap='4px'>
                        <Skeleton height='22px' />
                        <Skeleton height='22px' />
                        <Skeleton height='22px' />
                        <Skeleton height='22px' />
                        <Skeleton height='22px' />
                      </Box>
                    </Card>
                  </Box>
                )}
                {insuranceContacts?.length ? (
                  insuranceContacts?.map((agent: IMotorCarrierInsuranceContact) => (
                    <InsuranceAgentCard key={agent.id} agent={agent} />
                  ))
                ) : (
                  <Typography variant='caption' color='textSecondary'>
                    No Reported Data
                  </Typography>
                )}
              </Box>
              {agentsError && (
                <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={agentsError.message} />
              )}
              <Box m='16px 0' height='1px' width='100%' bgcolor={brandColors.coolGray3} />
            </div>
          )}
          {ready && (
            <div className='col-xs-12'>
              <p data-test='insurance-expiration-summary'>{summary}</p>
              {reviewer && id && (
                <p>
                  Reviewed by{' '}
                  <a
                    href={`${__ROOT__}/users/${parentUserId}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    data-test='insurance-reviewer'>
                    {reviewer.fullName}
                  </a>
                </p>
              )}
            </div>
          )}
        </div>

        {loading && <Spinner />}
        {error && <FeedbackMessage message={error} type='error' />}
        {ready && (
          <>
            <AutomobileLiability />
            <GeneralLiability />
            <CargoCoverageLiability />
            <TrailerInterchangeCoverage />
            <DishonestyCoverage />
            <WorkersCompensation />
          </>
        )}

        <InsuranceDialog />
      </Card>
    )
  }
)

export default Insurance
